import { getElementDimensions } from '@utils';
import { debounce } from 'es-toolkit';

class HeaderElement extends HTMLElement {
  connectedCallback() {
    this.lastScrollPosition = window.scrollY;
    this.getHeaderHeight();
    this.handleScroll();
    this.updateHeaderTop();

    window.addEventListener('resize', debounce(this.getHeaderHeight, 100));
    window.addEventListener('scroll', this.onScrollPassive, { passive: true });

    // Proactively close menus before a new one opens (capture ensures this runs first)
    document.addEventListener('click', this.onMenuControllerClickCapture, true);

    // Close menus when clicking typical actionable elements in the header
    this.addEventListener('click', (event) => {
      this.handleNavLinkEvent(event);
    });

    // Desktop UX: keep menus tidy on hover without breaking hover-to-open
    this.addEventListener('mouseover', (event) => {
      if (document.documentElement.classList.contains('touch')) return;
      const clickableElement = event.target.closest('a, button, [role="button"]');
      if (!clickableElement) return;
      if (clickableElement.closest('dropdown-element')) return;
      if (clickableElement.closest('mega-menu-dropdown')) return;

      // If hovering a mega menu controller, close others but keep the target pane
      const paneController = clickableElement.closest('[data-menu-pane-controller]');
      if (paneController) {
        const paneId = paneController.getAttribute('aria-controls');
        const nextPane = paneId ? document.getElementById(paneId) : null;
        this.closeAllMenus(nextPane);
        return;
      }

      // Otherwise, hovering another interactive element closes menus
      this.closeAllMenus();
    });

    // On page scroll, close any open menus quickly
    window.addEventListener('scroll', () => {
      this.closeAllMenus();
    });

    // Debounced - legacy support for dropdown-element hover open
    this.debouncedMouseover = debounce(() => {
      this.openDrawer?.open?.();
    }, 120);
  }

  /**
   * Close the open dropdown when handling a link that does not control a dropdown
   * @param {Event} event - The event object
   */
  handleNavLinkEvent(event) {
    const isDropdownController = !!event.target.closest('[data-dropdown-controller]');
    const isMenuPaneController = !!event.target.closest('[data-menu-pane-controller]');
    const isInsideDropdown = !!event.target.closest('dropdown-element');
    const isInsideMegaMenu = !!event.target.closest('mega-menu-dropdown');

    // If interacting with controllers or inside open menus, do not auto-close
    if (isDropdownController || isMenuPaneController || isInsideDropdown || isInsideMegaMenu)
      return;

    // Clicking/pressing typical controls in the header should close menus
    const clickableElement = event.target.closest('a, button, [role="button"], input, select');
    if (!clickableElement) return;
    this.closeAllMenus();
  }

  /**
   * Handle dropdown events in primary nav, find the dropdown and if it exists - handle the action
   * @param {Event} event - The event object
   */
  handleNavDrawerEvent(event) {
    const controller = event.target.closest('[data-dropdown-controller]');
    const dropdownId = controller?.getAttribute('aria-controls');
    if (!dropdownId) return;

    // On mobile (touch devices), close all other menus first
    if (document.documentElement.classList.contains('touch')) {
      this.closeAllMenus();
    }

    this.openDrawer = document.getElementById(dropdownId);
    this.debouncedMouseover();
  }

  /**
   * Close all dropdowns
   */
  closeAllNavDrawers() {
    const dropdowns = document.querySelectorAll('dropdown-element[aria-hidden="false"]');
    dropdowns.forEach((dropdown) => dropdown.close());
  }

  /**
   * Close all menus (dropdown-element and mega-menu-dropdown)
   * @param {HTMLElement} [except] - An element to keep open
   */
  closeAllMenus(except) {
    document.querySelectorAll('dropdown-element[aria-hidden="false"]').forEach((el) => {
      if (except && el === except) return;
      el.close();
    });

    document.querySelectorAll('mega-menu-dropdown[aria-expanded="true"]').forEach((el) => {
      if (except && el === except) return;
      el.close();
    });
  }

  /**
   * Capture click on a menu-pane controller early to ensure other panes close first
   */
  onMenuControllerClickCapture = (event) => {
    const controller = event.target.closest('[data-menu-pane-controller]');
    if (!controller) return;
    const paneId = controller.getAttribute('aria-controls');
    if (!paneId) return;
    const nextPane = document.getElementById(paneId);
    // Close everything except the pane that is about to be toggled
    this.closeAllMenus(nextPane);
  };

  /**
   * Set header height in global scope and CSS var
   */
  getHeaderHeight = () => {
    const headerHeight = getElementDimensions('header-element').height;
    window.HEADER_HEIGHT = headerHeight;
    document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
  };

  /**
   * Set header top in global scope and CSS var
   */
  updateHeaderTop = (top = 0) => {
    const headerTop = `${top * -1}px`;
    document.documentElement.style.setProperty('--header-top', headerTop);
  };

  /**
   * Handle scroll event
   */
  handleScroll = () => {
    const current = window.scrollY;
    // Toggle `.scrolled` so layout-specific CSS (e.g. header-turquoise.css)
    // can drive the shrink + backdrop-blur effect when the page is scrolled.
    this.classList.toggle('scrolled', current > 0);
    this.updateHeaderTop(0);
    this.lastScrollPosition = current;
  };

  // Passive scroll handler wrapper to keep things snappy
  onScrollPassive = () => {
    // Use rAF to avoid layout thrash during rapid scroll
    if (this._scrollRaf) return;
    this._scrollRaf = requestAnimationFrame(() => {
      this._scrollRaf = null;
      this.handleScroll();
    });
  };
}

customElements.define('header-element', HeaderElement);
